import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Box, Button, CircularProgress, Stack } from '@mui/material';

import { IncidentCodeApi, DataApi } from '@/api';
import { Page, PageHeader } from '@/components';
import { IIncidentCodePayload } from '@/models/incident.code';
import { ConfirmTypes, useToastStore, useDataStore } from '@/store';

import { IncidentCodeForm } from './components/IncidentCodeForm';

const FORM_ID = 'incident-code-add-form';

export const AddIncidentCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateToast } = useToastStore();
  const { updateData } = useDataStore();
  const [isSubmittingViaPage, setIsSubmittingViaPage] = useState(false);

  useEffect(() => {
    setIsSubmittingViaPage(false);
  }, [location.pathname]);

  const handleSubmitFromForm = async (data: IIncidentCodePayload) => {
    setIsSubmittingViaPage(true);
    try {
      await IncidentCodeApi.create(data);

      // Refresh incident codes in the data store
      try {
        const response = await DataApi.getIncidentCodes();
        if (response.data) {
          updateData({ incidentCodes: response.data });
        }
      } catch (refreshError) {
        updateToast({
          type: ConfirmTypes.ERROR,
          message: 'Failed to refresh incident codes:',
          open: true,
        });
      }

      updateToast({
        type: ConfirmTypes.SUCCESS,
        message: 'Incident Code has been added successfully',
        open: true,
      });
      navigate(
        `/admin/account-configuration/incident-codes${window.location.search}`,
      );
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        message: err.message || 'Failed to add incident code.',
        open: true,
      });
      throw err;
    } finally {
      setIsSubmittingViaPage(false);
    }
  };

  const handleCancel = () => {
    navigate(
      `/admin/account-configuration/incident-codes${window.location.search}`,
    );
  };

  const handleSave = () => {
    const form = document.getElementById(FORM_ID);
    if (form) {
      form.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true }),
      );
    } else {
      updateToast({
        open: true,
        message: 'Could not find form to submit.',
        type: ConfirmTypes.ERROR,
      });
    }
  };

  const pageTitle = 'Add Incident Code';

  return (
    <Page
      title={pageTitle}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <PageHeader
        title={pageTitle}
        backRoute={`/admin/account-configuration/incident-codes${window.location.search}`}
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
              disabled={isSubmittingViaPage}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSave}
              disabled={isSubmittingViaPage}
            >
              {isSubmittingViaPage ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Save'
              )}
            </Button>
          </Stack>
        }
      />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
        <IncidentCodeForm
          key={`new-incident-code-${Date.now()}`}
          formId={FORM_ID}
          onSubmit={handleSubmitFromForm}
          isEditMode={false}
        />
      </Box>
    </Page>
  );
};

export default AddIncidentCodePage;
